import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { EntityTypeConstants } from '../common/EntityTypeConstants';
import { Setting } from '../common/Setting';
import { AppProfileService } from './profile.service';
import { SettingService } from './setting.service';

export const LIST_PAGE_SIZE_SETTING = 'ListPageSize';
export const DEFAULT_LIST_PAGE_SIZE = 50;

/**
 * #94 / backend #651: the profile-scoped consent for personalized suggestions, stored as the
 * string "true" / "false" and defaulting to **false** (Art. 6(1)(a)). Being a profile setting
 * rather than localStorage, a withdrawal applies on every device (Art. 7(3)).
 *
 * Writing "false" DELETES the profile's personalization history in the same backend write, so
 * every caller that can switch it off has to warn first — see the settings page.
 */
export const PERSONALIZATION_ENABLED_SETTING = 'PersonalizationEnabled';

/**
 * #648: cached read access to the current user's profile-scoped settings.
 * Every list view sizes its pages from listPageSize$; the settings page calls
 * refresh() after saving so open list views pick the new size up on their
 * next load. A missing setting row (profiles created before the reseed), an
 * anonymous session or a failing request all fall back to the default 50 —
 * a settings problem must never break a list page.
 */
@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private profileService = inject(AppProfileService);
  private settingService = inject(SettingService);

  private refresh$ = new BehaviorSubject<void>(undefined);

  public readonly listPageSize$: Observable<number>;

  constructor() {
    this.listPageSize$ = combineLatest([
      this.profileService.currentProfile$.pipe(catchError(() => of(null))),
      this.refresh$,
    ]).pipe(
      switchMap(([profile]) =>
        !profile?.id
          ? of(DEFAULT_LIST_PAGE_SIZE)
          : this.settingService.getByReference(profile.id, EntityTypeConstants.EntityTypeProfile).pipe(
              map((settings) => this.parseListPageSize(settings)),
              catchError(() => of(DEFAULT_LIST_PAGE_SIZE)),
            ),
      ),
      shareReplay(1),
    );
  }

  public refresh(): void {
    this.refresh$.next();
  }

  private parseListPageSize(settings: Setting[]): number {
    const value = settings?.find((s) => s.name === LIST_PAGE_SIZE_SETTING)?.value;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 500 ? parsed : DEFAULT_LIST_PAGE_SIZE;
  }
}
